/* Jubi Rug Plan — photo upload + Klaviyo lead event.
   Photo uploads to the R2 worker the moment it's chosen; the returned URL
   fills a hidden field so it arrives inside the contact-form email.
   Submit is disabled while an upload is in flight. Marketing subscribe
   happens ONLY if the opt-in checkbox is ticked. */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';

  function init(root) {
    if (root.dataset.jrpInit) return;
    root.dataset.jrpInit = 'true';

    var form = root.querySelector('form.jrp__form, form[action*="/contact"]');
    var fileInput = root.querySelector('[data-jrp-file]');
    var uploadBox = root.querySelector('[data-jrp-upload-box]');
    var uploadLabel = root.querySelector('[data-jrp-upload-label]');
    var status = root.querySelector('[data-jrp-upload-status]');
    var thumbs = root.querySelector('[data-jrp-thumbs]');
    var photoField = root.querySelector('[data-jrp-photo-url]');
    var submitBtn = root.querySelector('[data-jrp-submit]');
    var subscribeBox = root.querySelector('[data-jrp-subscribe]');
    var uploading = false;

    var success = root.querySelector('.jrp__success');
    if (success) {
      /* Success view: hide the now-redundant intro, land at the top of the
         page (counteracting the contact-form anchor scroll), focus quietly */
      ['.jrp__heading', '.jrp__intro', '.jrp__steps'].forEach(function (sel) {
        var el = root.querySelector(sel);
        if (el) el.style.display = 'none';
      });
      /* Remove the redirect's #hash so the browser can't re-anchor to it
         after images load, then assert top now, next tick, and post-load */
      if (window.location.hash) {
        try {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (err) { /* ignore */ }
      }
      var toTop = function () { window.scrollTo(0, 0); };
      toTop();
      setTimeout(toTop, 0);
      window.addEventListener('load', function () { setTimeout(toTop, 0); });
      try {
        success.focus({ preventScroll: true });
      } catch (err) {
        success.focus();
      }
    }

    var source = 'direct';
    try {
      source = new URLSearchParams(window.location.search).get('src') || 'direct';
    } catch (err) { /* older browsers — keep 'direct' */ }
    var sourceField = root.querySelector('[data-jrp-source]');
    if (sourceField) sourceField.value = source;

    function setStatus(message, isError) {
      if (!status) return;
      status.hidden = !message;
      status.textContent = message || '';
      status.classList.toggle('is-error', !!isError);
    }

    /* Photos accumulate — each picker round ADDS (up to 3), and every
       thumb carries an × to remove just that one. Older/less-technical
       customers shouldn't need to know "reselect everything at once". */
    var photos = [];

    function syncPhotos() {
      if (photoField) photoField.value = photos.join('\n');

      if (thumbs) {
        thumbs.innerHTML = '';
        photos.forEach(function (url, i) {
          var wrap = document.createElement('span');
          wrap.className = 'jrp__thumb';
          var img = document.createElement('img');
          img.src = url;
          img.alt = 'Uploaded room photo ' + (i + 1);
          img.loading = 'lazy';
          var x = document.createElement('button');
          x.type = 'button';
          x.className = 'jrp__thumb-x';
          x.setAttribute('aria-label', 'Remove photo ' + (i + 1));
          x.textContent = '×';
          x.addEventListener('click', function (e) {
            /* thumbs live inside the upload <label> — without this the
               click would also pop the file picker */
            e.preventDefault();
            e.stopPropagation();
            photos.splice(i, 1);
            syncPhotos();
            setStatus('', false);
          });
          wrap.appendChild(img);
          wrap.appendChild(x);
          thumbs.appendChild(wrap);
        });
        thumbs.hidden = photos.length === 0;
      }

      if (uploadBox) uploadBox.classList.toggle('is-done', photos.length > 0);
      if (uploadLabel) {
        if (!photos.length) {
          uploadLabel.textContent = 'Add photos of your room';
        } else if (photos.length >= 3) {
          uploadLabel.textContent = '3 photos attached ✓';
        } else {
          uploadLabel.textContent = photos.length + (photos.length === 1 ? ' photo' : ' photos') + ' attached ✓ — tap to add another';
        }
      }
    }

    function uploadOne(file) {
      /* Turnstile token per upload (assets/jubi-turnstile.js); the upload Worker verifies it */
      var tokenP = window.JubiTurnstile ? window.JubiTurnstile.token(uploadBox) : Promise.resolve(null);
      return tokenP.then(function (tok) {
        var headers = { 'Content-Type': file.type || 'image/jpeg' };
        if (tok) headers['X-Turnstile-Token'] = tok;
        return fetch(root.dataset.uploadUrl, { method: 'POST', headers: headers, body: file });
      })
        .then(function (res) { return res.json(); })
        .then(function (data) { return data && data.url ? data.url : null; })
        .catch(function () { return null; });
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var files = Array.prototype.slice.call(fileInput.files || []);
        /* clear immediately so picking the SAME file again still fires */
        fileInput.value = '';
        if (!files.length) return;

        var room = 3 - photos.length;
        if (room <= 0) {
          setStatus('Three photos is plenty — tap the × on one to swap it out.', false);
          return;
        }

        var skippedLarge = files.length;
        files = files.filter(function (f) { return f.size <= 8 * 1024 * 1024; });
        skippedLarge -= files.length;
        var skippedExtra = Math.max(0, files.length - room);
        files = files.slice(0, room);

        if (!files.length) {
          setStatus('Those photos are over 8 MB each — most phones can export a smaller version.', true);
          return;
        }

        uploading = true;
        if (submitBtn) submitBtn.disabled = true;
        setStatus('Uploading ' + files.length + (files.length === 1 ? ' photo…' : ' photos…'), false);

        Promise.all(files.map(uploadOne)).then(function (urls) {
          var good = urls.filter(Boolean);
          photos = photos.concat(good);
          syncPhotos();

          var notes = [];
          if (good.length < files.length) notes.push((files.length - good.length) + ' upload(s) failed — you can still submit.');
          if (skippedLarge) notes.push(skippedLarge + ' photo(s) skipped (over 8 MB).');
          if (skippedExtra) notes.push('Only ' + room + ' more fit — the first ' + files.length + (files.length === 1 ? ' was' : ' were') + ' added.');
          setStatus(notes.join(' '), good.length < files.length);

          uploading = false;
          if (submitBtn) submitBtn.disabled = false;
        });
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        if (uploading) {
          e.preventDefault();
          setStatus('One second — your photo is still uploading.', false);
          return;
        }

        var companyId = root.dataset.klaviyoCompany;
        var emailInput = form.querySelector('input[name="contact[email]"]');
        var email = emailInput ? emailInput.value.trim() : '';
        if (!companyId || !email || !window.fetch) return;

        function field(name) {
          var el = form.querySelector('[name="contact[' + name + ']"]');
          return el ? el.value : '';
        }

        var eventPayload = {
          data: {
            type: 'event',
            attributes: {
              properties: {
                /* read the FIELD at submit time (not the init-time URL
                   param) — the modal stamps its trigger's src here */
                'Request Source': (sourceField && sourceField.value) || source,
                'Room': field('Room'),
                'Size': field('Size'),
                'Budget': field('Budget'),
                'Timeline': field('Timeline'),
                'Style': field('Style'),
                'Notes': field('Notes'),
                'Room Photos': photoField ? photoField.value : ''
              },
              metric: { data: { type: 'metric', attributes: { name: 'Requested Rug Plan' } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        };

        /* PDP-opened modal carries the viewed product — include when set */
        var viewingVal = field('Viewing');
        if (viewingVal) {
          eventPayload.data.attributes.properties['Viewing'] = viewingVal;
        }

        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(eventPayload)
        }).catch(function () {});

        if (subscribeBox && subscribeBox.checked && root.dataset.klaviyoList) {
          var subPayload = {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Rug Plan Request',
                profile: { data: { type: 'profile', attributes: { email: email } } }
              },
              relationships: { list: { data: { type: 'list', id: root.dataset.klaviyoList } } }
            }
          };
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(subPayload)
          }).catch(function () {});
        }
        /* form submits natively → Shopify emails the store address */
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-rug-plan]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target.querySelector('[data-rug-plan]');
    if (el) init(el);
  });
})();
